/**
 * Authorization model.
 *
 * Roles are *not* checked directly in route handlers. A role resolves to a set
 * of permissions, and handlers assert permissions. That indirection is the
 * whole point: adding "Accountant" or "Branch Manager" later is a new entry in
 * `ROLE_PERMISSIONS` rather than an audit of every `if (role === 'admin')` in
 * the codebase.
 *
 * A member may also carry per-user overrides in `business_members.permissions`
 * (grant/revoke), applied on top of their role by `resolvePermissions`.
 */

import { MEMBER_ROLES, type MemberRole, type ModuleKey } from './enums.js';

export const PERMISSIONS = [
  'dashboard:view',

  'sales:read',
  'sales:create',
  'sales:update',
  'sales:delete',
  'sales:void',
  'payments:create',
  'payments:delete',

  'expenses:read',
  'expenses:create',
  'expenses:update',
  'expenses:delete',
  /**
   * Signing off somebody else's spending.
   *
   * Declared now and not yet enforced anywhere: the approval workflow it
   * belongs to arrives with the accountability work. Named here so the role
   * it will attach to exists before the screen that uses it, rather than
   * inventing an approval gate that approves nothing.
   */
  'expenses:approve',
  'expense_categories:manage',

  'customers:read',
  'customers:create',
  'customers:update',
  'customers:delete',

  'staff:read',
  'staff:create',
  'staff:update',
  'staff:delete',
  /**
   * Deciding where people work.
   *
   * Distinct from `staff:update`, which edits a person's record. This one
   * assigns them to locations, and the two are different authorities: a
   * supervisor may well correct a phone number without being the person who
   * decides who covers which shop.
   */
  'staff:manage',

  'branches:read',
  'branches:create',
  'branches:update',
  'branches:delete',

  'channels:read',
  'channels:manage',

  'products:read',
  'products:manage',

  'stock:read',
  'stock:manage',
  /**
   * Walking the shelf and writing down what is there.
   *
   * Deliberately separate from `stock:manage`. Counting records evidence and
   * changes nothing; adjusting stock by hand changes the books. A storekeeper
   * needs the first and should not have the second, and the whole value of a
   * count is that the person who did it could not simply have edited the
   * figure instead.
   */
  'stock:count',

  /**
   * Receiving a delivery.
   *
   * Separate from `stock:manage` because they are different authorities that
   * happen to both raise stock. Receiving records goods that physically
   * arrived, against a supplier and an invoice somebody can check; adjusting
   * raises or lowers the books on one person's say-so. A storekeeper signing
   * for a van needs the first, and giving them the second as a side effect is
   * how stock leaves without a trace.
   */
  'receiving:read',
  'receiving:manage',
  'suppliers:read',
  'suppliers:manage',

  'reports:read',
  'reports:export',

  // Closing a till. Recording what was counted is one job; deciding whether a
  // count reconciles — approving it, or disputing a shortage — is another, and
  // the person who counted is the wrong person to do the second.
  'cash_counts:create',
  'cash:reconcile',
  /**
   * Handing over cash from an account you hold, to be banked (0154). It stays
   * pending — out of your cash, not yet in the bank — until somebody with
   * `cash:reconcile` confirms it arrived. Granted to a worker or a counter
   * that holds a cash float; in no role by default except owner and admin.
   */
  'cash:deposit',

  // Spending the business's AI allowance. Deliberately its own permission
  // rather than folded into `reports:read`: reading the sales ledger is free,
  // and asking the assistant is not.
  'ai:use',
  /**
   * Sharing an AI project with a colleague.
   *
   * Separate from `ai:use` because using your own project is not a disclosure
   * and sharing one is: it makes conversations their author has marked
   * shareable readable by somebody else. Default admin and above; grantable to
   * staff by override.
   *
   * It does NOT widen what the business data in an answer may contain. Every
   * turn's snapshot is still built from the ASKING user's permissions, so a
   * viewer who cannot see expenses gets no expense figures in a new answer,
   * whoever shared the project with them.
   */
  'ai:projects_share',

  'members:read',
  'members:invite',
  'members:update',
  'members:remove',

  'settings:read',
  'settings:update',
  /**
   * The settings that cost money or change who can do what.
   *
   * Declared now and not yet enforced: `settings:update` currently gates both
   * editing a business phone number and changing the subscription plan, which
   * is an overload worth splitting — but re-gating billing is a behaviour
   * change with its own migration of existing members, not a side effect of
   * adding locations.
   */
  'settings:manage',

  /**
   * Seeing the delivery book.
   *
   * Reading only, and only where the Delivery module is enabled — a retail shop
   * has no deliveries and should not be offered a screen for them. The rider's
   * payout is money leaving the business, so this is a reporting permission
   * rather than an operational one.
   */
  'deliveries:read',
  /**
   * Entering and correcting a delivery.
   *
   * Separate from reading, because they are different jobs: a worker at the
   * counter records the run they just did, while seeing the whole location's
   * book — every fee, every rider's earnings — is a supervisor's view.
   */
  'deliveries:manage',
  /**
   * Setting the owner-approved delivery prices.
   *
   * A counter's fee is checked against these rules. Changing them changes what
   * every counter may charge without asking, so it is a management authority.
   */
  'delivery_pricing:manage',
  /**
   * Deciding a delivery's exceptions: a fee outside the approved prices
   * (including ₦0), or a date before today. Never the person who asked, and
   * never a location login.
   */
  'deliveries:approve_exceptions',
  /**
   * Confirming that an electronic payment for a delivery actually arrived.
   *
   * Until somebody holding this checks the proof, a transfer, card or wallet
   * payment is "pending verification": the delivery is done, the money is
   * claimed, and nothing is posted as revenue.
   */
  'deliveries:verify_payment',
  /**
   * Saying whether a rider's payout was checked against what they were owed.
   *
   * A review status — Verified, Under Review, Not Verified — that moves no
   * money. Separate from `deliveries:verify_payment`, which is about the
   * customer's money arriving; this is about the rider's side. Owners and
   * admins, never a counter or a location login.
   */
  'deliveries:verify_payout',
  /**
   * Deleting a delivery outright.
   *
   * Separate from `deliveries:manage`, which records and edits. Removing a job
   * from the books is a different authority: it reverses whatever the delivery
   * posted and takes it out of every figure, so it sits with voiding a sale
   * and deleting a payment rather than with entering one.
   *
   * Held by the owner alone, and refused to a location login outright — the
   * counter that recorded a delivery is the wrong person to decide it never
   * happened.
   */
  'deliveries:delete',
  /**
   * Deleting a delivery whose money has already arrived.
   *
   * Narrower than `deliveries:delete`, and held by the owner alone. An admin
   * may remove a delivery that was entered wrongly and never paid; writing off
   * money a customer actually handed over is the owner's decision and nobody
   * else's.
   *
   * A separate permission rather than `if (role === 'owner')` for the reason
   * at the top of this file: the authority is stated once, here, instead of
   * being re-derived at every call site that happens to care.
   */
  'deliveries:delete_paid',

  /**
   * ── The fleet, and the money owed on it ───────────────────────────────────
   *
   * Four permissions, not one, because they are four different authorities and
   * the business already has people holding only some of them.
   */

  /** Seeing the bike register: plates, models, who holds what. */
  'bikes:read',
  /**
   * Adding a bike, correcting its details, and reassigning it to a rider.
   *
   * A bike is an asset worth more than a month of the business's revenue, and
   * reassigning it moves it into somebody's hands. Not a counter task.
   */
  'bikes:manage',
  /**
   * Taking in a rider's weekly remittance.
   *
   * Collection, deliberately separate from `remittance:manage`: somebody at
   * the hub counts the cash a rider hands over, and recording that should not
   * also let them decide the rider owes nothing. Each payment records who took
   * it, which is the point of letting staff hold it at all.
   */
  'remittance:collect',
  /**
   * Deciding what a rider owes — accruing a period, retyping an amount, and
   * waiving a week.
   *
   * A waiver forgives revenue the company has already recognised, so it is an
   * owner's or admin's call and it is attributed to them in the row.
   */
  'remittance:manage',
  /** Seeing hire purchase agreements and how far each rider has got. */
  'hire_purchase:read',
  /**
   * Writing an agreement, changing its terms, and recording an instalment.
   *
   * One permission rather than a read/collect/manage trio: an instalment is
   * only meaningful against terms somebody authorised, and the number of
   * agreements is small enough that whoever authorises them also receives on
   * them. Splitting collection out can follow if that stops being true.
   */
  'hire_purchase:manage',

  /**
   * ── Loans ─────────────────────────────────────────────────────────────────
   *
   * Money the business lends to riders and staff, and money it borrows.
   * Separate from hire purchase, which sells a bike on terms: a loan is cash.
   *
   * None of these is in the staff role. Lending the business's money is a
   * finance decision, so a finance person is given exactly the keys they need
   * (view, create, record repayments) by a grant on their membership.
   */
  /** Seeing loans, their schedules, and the loan reports. */
  'loans:read',
  /** Issuing a loan, or saving one as a draft. */
  'loans:create',
  /**
   * Recording a repayment, including a deduction from a rider's remittance or
   * payout under an agreement somebody with `loans:manage` authorised.
   */
  'loans:repay',
  /**
   * Correcting a loan: editing, cancelling, reversing a repayment, and
   * authorising or revoking a rider's deduction agreement.
   */
  'loans:manage',
  /**
   * Writing a loan off — forgiving money owed to the business. The owner's
   * alone by default, like deleting a delivery a customer has paid for.
   */
  'loans:write_off',
  /**
   * Recording what the business has confirmed about lending in a country —
   * whether it may charge interest or fees, to whom, and at what most. A
   * compliance statement, so the owner's alone by default.
   */
  'loans:configure',

  /**
   * ── Investments ────────────────────────────────────────────────────────────
   *
   * Investors' vehicle partnerships, equity and profit shares. Reading them is
   * an administrator's; changing an investment — its terms, dates, ownership,
   * scope, the vehicles linked to it — and posting money to or from one
   * (declaring a dividend, posting a profit share, paying an investor) are the
   * owner's, or whoever the owner grants them to. Money moves under them, and
   * a percentage typed wrong is somebody else's money.
   */
  'investments:read',
  'investments:manage',
  'investments:post',

  /**
   * ── Pharmacy ──────────────────────────────────────────────────────────────
   *
   * Split the way a pharmacy actually divides authority: who may sell, who may
   * move stock, who may decide stock is fit to sell again, and who may make a
   * clinical decision. A cashier holds the first; a stockkeeper the second;
   * the third and fourth are a pharmacist's. None is in the staff role —
   * each is granted through a pharmacy job preset (PHARMACY_JOB_PRESETS) on
   * an individual account, so nobody acquires clinical access by being staff.
   */
  /** Seeing the medicine catalogue, batches, expiry and stock levels. */
  'pharmacy:read',
  /** Adding and editing medicines, selling units, barcodes and prices. */
  'pharmacy:catalogue',
  /**
   * Deciding what an item legally is: whether it is a medicine, and its class
   * (OTC, pharmacy-only, prescription-only, restricted). A pharmacist's call,
   * audited with a reason; never a cashier's.
   */
  'pharmacy:classify',
  /** Selling at the pharmacy till, holding carts and confirming payments taken. */
  'pharmacy:sell',
  /** Giving a discount at the till, up to the configured limit. */
  'pharmacy:discount',
  /** Refunding a sale. Never restocks a medicine by itself — see `pharmacy:disposition`. */
  'pharmacy:refund',
  /** Deciding what happens to returned stock: back to saleable, quarantine or disposal. */
  'pharmacy:disposition',
  /** Recording adjustments, damage, counts and quarantine of stock. */
  'pharmacy:stock',
  /** Approving write-offs and disposals, and releasing quarantined stock. */
  'pharmacy:stock_approve',
  /** Purchase orders, receiving against them, supplier invoices, payments and returns. */
  'pharmacy:purchasing',
  /** Dispatching and receiving stock between locations. */
  'pharmacy:transfers',
  /** Opening and closing a till, and reconciling it. */
  'pharmacy:till',
  /**
   * Entering, after an outage, a sale that was made on paper — dated when it
   * happened. A manager's authority: it writes history, so it is in no job
   * preset and only the owner and admins hold it by default.
   */
  'pharmacy:outage_entry',
  /** Pharmacy settings: alert thresholds, discount limits, restricted lists. */
  'pharmacy:settings',
  /** Seeing prescriptions and their attachments. Clinical. */
  'prescriptions:read',
  /** Recording a prescription received. */
  'prescriptions:manage',
  /** Pharmacist review: approving quantities and the directions printed on labels. */
  'prescriptions:review',
  /** Dispensing against an approved prescription. */
  'prescriptions:dispense',
  /** Patient records: allergies, notes, medication history. Clinical. */
  'patients:read',
  'patients:manage',
  /** Dispensing a restricted (controlled) medicine, and reading its register. */
  'restricted:dispense',
  'restricted:read',
  /** Logging fridge and room temperatures. */
  'storage:log',
  /** Deciding a temperature excursion: quarantine, release or dispose. */
  'storage:decide',
  /** Opening a recall, quarantining affected batches and recording actions. */
  'recalls:manage',


  'audit:read',
  'api_keys:manage',

  /**
   * Uploading, reviewing, committing and rolling back an Excel import.
   *
   * Deliberately its own permission rather than folded into `settings:update`.
   * An import writes financial history in bulk and can reverse it again, which
   * is a strictly larger power than any single create permission grants — and
   * the staff role, which may record today's sales, has no business rewriting
   * three years of them.
   */
  'imports:manage',

  'business:delete',
  'business:transfer',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** Everything. Owners get this set. */
const ALL: readonly Permission[] = PERMISSIONS;

/**
 * Admins run the business day to day but cannot destroy or hand it over —
 * those two stay with the owner so a compromised admin account cannot
 * permanently remove the tenant.
 */
const ADMIN: readonly Permission[] = PERMISSIONS.filter(
  // `deliveries:delete_paid` is the owner's: an admin may remove a delivery
  // entered wrongly, never one a customer has paid for.
  // `loans:write_off` likewise: forgiving a debt is the owner's call.
  (p) =>
    p !== 'business:delete' &&
    p !== 'business:transfer' &&
    p !== 'deliveries:delete_paid' &&
    p !== 'loans:write_off' &&
    p !== 'loans:configure' &&
    // Changing investments and posting investor money: the owner's, unless granted.
    p !== 'investments:manage' &&
    p !== 'investments:post',
);

/**
 * Staff record the day's activity. Deliberately no delete rights on financial
 * records — corrections go through `sales:void`, which leaves an audit trail,
 * and voiding is an admin action.
 */
/**
 * Pharmacy permissions are never part of a role by default except owner and
 * admin. Staff receive them through a job preset, one account at a time.
 */
const PHARMACY_PERMISSIONS = PERMISSIONS.filter(
  (p) =>
    p.startsWith('pharmacy:') ||
    p.startsWith('prescriptions:') ||
    p.startsWith('patients:') ||
    p.startsWith('restricted:') ||
    p.startsWith('storage:') ||
    p === 'recalls:manage',
);
export { PHARMACY_PERMISSIONS };

const STAFF: readonly Permission[] = [
  'dashboard:view',
  'sales:read',
  'sales:create',
  'sales:update',
  'payments:create',
  'expenses:read',
  'expenses:create',
  'customers:read',
  'customers:create',
  'customers:update',
  'staff:read',
  'branches:read',
  'channels:read',
  'products:read',
  'stock:read',
  'stock:count',
  // Signing for a delivery is a storekeeper's job. Posting it still records
  // who did, and it can only raise stock against a named supplier.
  'receiving:read',
  'receiving:manage',
  'suppliers:read',
  'reports:read',
  // Counting the till at close. Approving the count is `cash:reconcile`.
  'cash_counts:create',
  'deliveries:read',
  // Recording the run they just did is the job. The module gate decides
  // whether this business has deliveries at all, so a shop's staff never see it.
  'deliveries:manage',
  // The fleet, read-only, and the cash a rider hands over.
  //
  // Staff see which bike a rider is on and take in the weekly remittance —
  // that is a counter job and the person doing it is recorded against every
  // payment. They cannot reassign a bike, waive a week, or write an agreement:
  // those give away an asset or forgive revenue, and `bikes:manage`,
  // `remittance:manage` and `hire_purchase:manage` are absent on purpose.
  'bikes:read',
  'remittance:collect',
  'hire_purchase:read',
  // Staff may ask the assistant. They are the ones on the counter with a
  // question, and a permission that only owners hold is a feature nobody uses.
  'ai:use',
  'settings:read',
];

/**
 * Somebody who works a shift, not somebody who runs the business.
 *
 * Deliberately tiny. A courier recording the run they just did needs to record
 * it and to see what they have recorded — and nothing else. `staff` carries
 * twenty-four permissions including the customer list, the staff list, reports,
 * products, receiving and suppliers: a shop manager's set, correct for a shop
 * manager, and far too much for a rider who visits once a day.
 *
 * `branches:read` is here because it is how the app answers "which locations
 * may I work in" — the question the worker link asks on their behalf. It shows
 * them their own locations and no others, because row level security has
 * already removed the rest.
 *
 * Everything absent from this list is absent on purpose. Adding to it should
 * feel like a decision, which is why it is short enough to read in one glance.
 */
const WORKER: readonly Permission[] = ['deliveries:read', 'deliveries:manage', 'branches:read'];

/**
 * A location, operating itself.
 *
 * ── The boundary is not in this list ───────────────────────────────────────
 *
 * Every permission here is read through ONE assigned location, because a
 * location account's membership carries exactly one `business_member_locations`
 * row and `app_branch_allowed()` filters on it in the database. `customers:read`
 * does not mean the business's customer list; it means the customers this
 * location has served — which `customer_branches` records and
 * `app_customer_visible()` enforces.
 *
 * That last sentence was false until October 2026: `customers` carried no
 * branch, so the policy asked only whether the caller had any location at all
 * and every counter read the whole book. The claim stays here because it is
 * the intent; what changed is that the database now agrees with it. No entry
 * here can widen that, and nothing an owner later grants can either — which is
 * the point of enforcing scope below the API
 * rather than inside the role.
 *
 * ── What is absent, and why ────────────────────────────────────────────────
 *
 * No `reports:read`, so no Reports, no Finance, no Money & Stock and no
 * dashboard. No `settings:read`, which also keeps Billing and the module list
 * out. No staff, members, imports or API keys. A shared login that anyone at
 * the counter can use is the wrong place to read what the company earned, and
 * "the till can see the company's margin" is not a mistake you get to make
 * twice.
 *
 * Sales and expenses are deliberately NOT here. The till is the owner's
 * explicit decision per counter (LOCATION_TILL_PERMISSIONS), live only while
 * the business runs Sales — never handed over on the strength of a role name
 * or a module being switched on.
 */
const LOCATION_OPERATOR: readonly Permission[] = [
  // How the app asks "which location am I". Returns the one, never a list.
  'branches:read',
  // Counting the cash in the drawer at close. The counter never sees what was
  // expected, cannot backdate a count, and cannot approve one: reconciling is
  // `cash:reconcile`, which a location login can never hold.
  'cash_counts:create',
  'deliveries:read',
  'deliveries:manage',
  'customers:read',
  'customers:create',
  'customers:update',
];

/**
 * What a machine may do, and the shape of the question.
 *
 * A credential is a long-lived secret sitting in another system's environment.
 * It will eventually be in a log, a backup, a screenshot of a deploy config, or
 * a repository somebody made public — so the only sound way to size this list
 * is to ask what the worst case looks like, not what would be convenient.
 *
 * `sales:create` is here because recording a delivery writes the revenue it
 * produced: this is the authority to record money, which is exactly what
 * ingestion is for. `deliveries:read` is here so a sender can confirm what
 * CenoX holds and reconcile against it.
 *
 * ── What is absent, and why ────────────────────────────────────────────────
 *
 * No `sales:read`. A leaked key must not be able to read back the books — it
 * posts deliveries, and nothing it posts requires knowing what anything else
 * earned. No `reports:read`, for the same reason and more so. No `sales:void`,
 * `sales:delete` or `expenses:delete`: a cancellation arrives as an event and
 * is applied by the ingestion path, which voids the delivery's own sale through
 * the ordinary path — a credential that could void arbitrary sales could erase
 * a quarter. No settings, members, staff, imports or exports.
 *
 * `customers:*` is included only to the extent ingestion needs it: a delivery
 * names a customer, and linking one is part of writing the delivery down.
 * Reading the customer list is not — hence no broader grant.
 */
const INTEGRATION: readonly Permission[] = [
  'deliveries:read',
  'deliveries:manage',
  // Recording a delivery writes the revenue it produced.
  'sales:create',
  'customers:read',
  'customers:create',
  'customers:update',
];

export const ROLE_PERMISSIONS: Record<MemberRole, readonly Permission[]> = {
  owner: ALL,
  admin: ADMIN,
  staff: STAFF,
  worker: WORKER,
  location_operator: LOCATION_OPERATOR,
  integration: INTEGRATION,
};

/**
 * Permissions a location login may hold, given what the business actually runs.
 *
 * The role is the floor; the modules decide what is added to it. Ceno Hub has
 * Sales switched off, so its Holy Ghost login gets no `sales:*` however the
 * role is written — a permission that exists because of a category rather than
 * because of this business is exactly the drift this closes.
 *
 * `extras` is what the owner has granted this particular login on top. It is
 * filtered through the same module gate, so granting expense entry to a
 * business that has Expenses switched off quietly grants nothing rather than
 * leaving a permission that springs to life later.
 */
export function locationOperatorPermissions(
  activeModules: readonly ModuleKey[],
  extras: readonly Permission[] = [],
): Permission[] {
  /*
    Exactly what a request will be allowed, so the access screen and the
    server can never disagree again. They did once: this function used to add
    the till whenever Sales was switched on, the screen said so, and no request
    ever received it. Till access is now the owner's explicit decision — see
    LOCATION_TILL_PERMISSIONS — and switching a module on grants nothing.
  */
  return [...locationOperatorRuntimePermissions(activeModules, { grant: [...extras] })];
}

/**
 * Sales Till Access: what an owner switches on for a counter, as one decision.
 *
 * Take a customer's money at this location, and see this location's sales —
 * nothing more. Editing, voiding, deleting or refunding a sale, removing a
 * payment and changing a price are all in LOCATION_FORBIDDEN, so no grant can
 * add them. Live only while the business runs Sales: the module filter below
 * drops all three when it is off, without touching the stored setting.
 *
 * Not on the general grant list: these are set through the till-access switch
 * and nowhere else, so a counter never ends up with half a till.
 */
export const LOCATION_TILL_PERMISSIONS = ['sales:read', 'sales:create', 'payments:create'] as const;

/** True when a stored grant list holds the whole till. */
export function hasTillAccess(grants: readonly string[] | null | undefined): boolean {
  return LOCATION_TILL_PERMISSIONS.every((p) => grants?.includes(p));
}

/**
 * Permissions that make no sense without the module behind them.
 *
 * Only the ones a location login could plausibly be granted. A permission
 * absent here is simply not module-gated.
 */
const PERMISSION_REQUIRES_MODULE: Partial<Record<Permission, ModuleKey>> = {
  ...Object.fromEntries(PHARMACY_PERMISSIONS.map((p) => [p, 'pharmacy' as ModuleKey])),
  'sales:read': 'sales_pos',
  'sales:create': 'sales_pos',
  'sales:update': 'sales_pos',
  'payments:create': 'sales_pos',
  'deliveries:read': 'delivery',
  'deliveries:manage': 'delivery',
  'delivery_pricing:manage': 'delivery',
  'deliveries:approve_exceptions': 'delivery',
  'deliveries:verify_payment': 'delivery',
  'deliveries:verify_payout': 'delivery',
  'deliveries:delete': 'delivery',
  'deliveries:delete_paid': 'delivery',
  'products:read': 'inventory',
  'stock:read': 'inventory',
  'stock:count': 'inventory',
  'receiving:read': 'inventory',
  'receiving:manage': 'inventory',
};

/**
 * What an owner may never grant a location login.
 *
 * Not because the location boundary would fail — it would hold — but because
 * each of these reads or changes something that belongs to the business rather
 * than to one counter. A shared password on a wall is the wrong credential for
 * any of them.
 */
const LOCATION_FORBIDDEN: ReadonlySet<Permission> = new Set<Permission>([
  /*
    Changing money after it was taken, and changing what things cost.

    A counter may spend the business's money where an owner has said so and,
    with Sales Till Access, take a customer's money at the price on file. It
    may never go back and change either: editing or deleting an expense,
    editing, voiding or deleting a sale, or removing a payment (which is how a
    refund is recorded) is how a recorded number becomes a different one, and
    the person who typed it is the wrong person to decide that. Prices belong
    to the catalogue, which is `products:manage`.

    `expenses:create` is absent deliberately — a counter that cannot record
    what it spent keeps no books at all.
  */
  'expenses:update',
  'expenses:delete',
  'sales:update',
  'sales:void',
  'sales:delete',
  'payments:delete',
  'products:manage',
  // Approving its own count, or anybody's.
  'cash:reconcile',

  // Pricing a delivery and deciding its exceptions belong to whoever answers
  // for the money, never to the counter that asked.
  'delivery_pricing:manage',
  'deliveries:approve_exceptions',
  'deliveries:verify_payment',
  'deliveries:verify_payout',
  // Deleting a delivery reverses money that was already posted.
  'deliveries:delete',
  'deliveries:delete_paid',
  'reports:read',
  'reports:export',
  'dashboard:view',
  'settings:read',
  'settings:update',
  'settings:manage',
  'members:read',
  'members:invite',
  'members:update',
  'members:remove',
  'staff:read',
  'staff:create',
  'staff:update',
  'staff:delete',
  'staff:manage',
  'imports:manage',
  'api_keys:manage',
  'audit:read',
  'bikes:read',
  'bikes:manage',
  'remittance:collect',
  'remittance:manage',
  'hire_purchase:read',
  'hire_purchase:manage',
  'loans:read',
  'loans:create',
  'loans:repay',
  'loans:manage',
  'loans:write_off',
  'loans:configure',
  // Investors' money belongs to the business, never to a counter.
  'investments:read',
  'investments:manage',
  'investments:post',
  'business:delete',
  'business:transfer',
  'branches:create',
  'branches:update',
  'branches:delete',
  /*
    Pharmacy, all of it. A shared counter password is the wrong credential
    for a dispensing decision, a patient's allergies or a controlled-drug
    register, and the till here sells medicines whose dispenser must be a
    named person. Pharmacy staff sign in as themselves.
  */
  'pharmacy:read',
  'pharmacy:catalogue',
  'pharmacy:classify',
  'pharmacy:sell',
  'pharmacy:discount',
  'pharmacy:refund',
  'pharmacy:disposition',
  'pharmacy:stock',
  'pharmacy:stock_approve',
  'pharmacy:purchasing',
  'pharmacy:transfers',
  'pharmacy:till',
  'pharmacy:outage_entry',
  'pharmacy:settings',
  'prescriptions:read',
  'prescriptions:manage',
  'prescriptions:review',
  'prescriptions:dispense',
  'patients:read',
  'patients:manage',
  'restricted:dispense',
  'restricted:read',
  'storage:log',
  'storage:decide',
  'recalls:manage',
]);

/** What an owner may offer on a location login's access screen. */
export const LOCATION_GRANTABLE_PERMISSIONS: readonly Permission[] = PERMISSIONS.filter(
  (p) =>
    !LOCATION_FORBIDDEN.has(p) &&
    !ROLE_PERMISSIONS.location_operator.includes(p) &&
    // Through the till-access switch only. See LOCATION_TILL_PERMISSIONS.
    !(LOCATION_TILL_PERMISSIONS as readonly Permission[]).includes(p),
);

export interface PermissionOverrides {
  /** Permissions granted on top of the role's set. */
  grant?: Permission[];
  /** Permissions removed from the role's set. Applied after `grant`. */
  revoke?: Permission[];
}

/**
 * Effective permissions for a member: role defaults, plus grants, minus
 * revokes. Revoke wins over grant so a revocation cannot be bypassed by also
 * listing the permission as a grant.
 */
export function resolvePermissions(
  role: MemberRole,
  overrides?: PermissionOverrides | null,
): Set<Permission> {
  const effective = new Set<Permission>(ROLE_PERMISSIONS[role]);

  for (const p of overrides?.grant ?? []) {
    if (isPermission(p)) effective.add(p);
  }
  for (const p of overrides?.revoke ?? []) {
    if (isPermission(p)) effective.delete(p);
  }
  return effective;
}

/**
 * What the screen should offer a member: their role, their grants and
 * revocations, and — for a location login — never anything a location may not
 * hold, whatever a stored grant says. The same rules the server applies; the
 * server still decides every request.
 */
export function memberPermissions(
  role: MemberRole,
  overrides?: { grant?: readonly string[] | undefined; revoke?: readonly string[] | undefined } | null,
): Set<Permission> {
  const effective = resolvePermissions(role, overrides as PermissionOverrides | null | undefined);
  if (role === 'location_operator') {
    for (const p of LOCATION_FORBIDDEN) effective.delete(p);
  }
  return effective;
}

/**
 * What a location login may actually do on a request.
 *
 * `resolvePermissions` applies whatever overrides are stored. For a location
 * login that is not enough: the access screen refuses forbidden grants when
 * they are written, but a row written any other way — a restore, a migration,
 * a fix by hand in SQL, an endpoint added later — would take effect silently.
 * So the same two rules are held again here, where the request is decided:
 *
 *   - nothing in `LOCATION_FORBIDDEN`, whatever the row says;
 *   - nothing whose module this business has switched off.
 *
 * Only ever narrows. The role's own set and any lawful grant pass through
 * unchanged, and nothing is added that the stored row did not already give —
 * the Sales extras `locationOperatorPermissions` shows on the access screen
 * are not switched on by this.
 */
export function locationOperatorRuntimePermissions(
  activeModules: readonly ModuleKey[],
  overrides?: PermissionOverrides | null,
): Set<Permission> {
  const active = new Set(activeModules);
  const effective = resolvePermissions('location_operator', overrides);

  for (const p of [...effective]) {
    const module = PERMISSION_REQUIRES_MODULE[p];
    if (LOCATION_FORBIDDEN.has(p) || (module && !active.has(module))) effective.delete(p);
  }
  return effective;
}

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/** True when `role` is at least as privileged as `minimum`. */
export function roleAtLeast(role: MemberRole, minimum: MemberRole): boolean {
  return MEMBER_ROLES.indexOf(role) >= MEMBER_ROLES.indexOf(minimum);
}

/**
 * Whether `actor` may assign or modify `target`'s role. A member can never
 * act on someone at or above their own level — this is what stops an admin
 * from demoting the owner or promoting themselves.
 */
export function canManageRole(actorRole: MemberRole, targetRole: MemberRole): boolean {
  return MEMBER_ROLES.indexOf(actorRole) > MEMBER_ROLES.indexOf(targetRole);
}


/**
 * Pharmacy job presets: what a pharmacist, cashier or stockkeeper is given.
 *
 * Applied to a member on the `staff` role as grants and revocations, so each
 * person keeps an individual account and the change is one audited write —
 * not a new role that every `roleAtLeast` comparison would have to learn.
 * The owner is the `owner` role and needs no preset.
 *
 * Deliberately conservative:
 *   - a cashier sells and confirms what they took, and sees no clinical data;
 *   - a stockkeeper receives, counts, transfers and logs temperatures, and
 *     cannot sell, approve a write-off or release quarantined stock;
 *   - a pharmacist reviews, dispenses, holds the restricted register and
 *     decides on quarantine and returned stock.
 */
export const PHARMACY_JOB_PRESETS = ['pharmacist', 'cashier', 'stockkeeper'] as const;
export type PharmacyJobPreset = (typeof PHARMACY_JOB_PRESETS)[number];

export const PHARMACY_JOB_LABELS: Record<PharmacyJobPreset, string> = {
  pharmacist: 'Pharmacist',
  cashier: 'Cashier',
  stockkeeper: 'Stockkeeper',
};

export const PHARMACY_JOB_PERMISSIONS: Record<
  PharmacyJobPreset,
  { grant: readonly Permission[]; revoke: readonly Permission[] }
> = {
  pharmacist: {
    grant: [
      'pharmacy:read',
      // Medicine records, and what each one legally is.
      'pharmacy:catalogue',
      'pharmacy:classify',
      'pharmacy:sell',
      'pharmacy:discount',
      'pharmacy:refund',
      'pharmacy:disposition',
      'pharmacy:stock',
      'pharmacy:stock_approve',
      'pharmacy:transfers',
      'pharmacy:till',
      'prescriptions:read',
      'prescriptions:manage',
      'prescriptions:review',
      'prescriptions:dispense',
      'patients:read',
      'patients:manage',
      'restricted:dispense',
      'restricted:read',
      'storage:log',
      'storage:decide',
      'recalls:manage',
    ],
    revoke: [],
  },
  cashier: {
    grant: ['pharmacy:sell', 'pharmacy:till'],
    // A cashier at a pharmacy does not sign for deliveries or count stock,
    // and never edits a medicine or decides what class it is.
    revoke: ['receiving:manage', 'stock:count', 'expenses:create', 'pharmacy:catalogue', 'pharmacy:classify'],
  },
  stockkeeper: {
    grant: [
      'pharmacy:read',
      'pharmacy:stock',
      'pharmacy:transfers',
      'pharmacy:purchasing',
      'storage:log',
    ],
    revoke: ['sales:create', 'payments:create', 'sales:update'],
  },
};
